import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField as MuiTextField, TextFieldProps } from '@mui/material';
import cn from 'classnames';
import React, { forwardRef, useEffect, useState } from 'react';
import { RegisterOptions, UseFormRegister } from 'react-hook-form';

import styles from './styles.module.scss';

interface InputProps {
  wrapperClassName?: string;
  inputClass?: string;
  register?: UseFormRegister<any>;
  registerOptions?: RegisterOptions;
  errors?: { [key: string]: any };
  wrapperStyle?: React.CSSProperties;
  type?: string;
  name: string;
  label?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  disabled?: boolean;
  maxLength?: number;
  autoComplete?: string;
  value?: any;
  required?: boolean;
  showPasswordToggle?: boolean;
  multiline?: boolean | undefined;
  minRows?: string | number | undefined;
  maxRows?: string | number | undefined;
  hideLabel?: boolean;
  placeholder?: string;
  InputProps?: TextFieldProps['InputProps'];
  inputProps?: TextFieldProps['inputProps'];
}

const CustomTextField = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      inputClass,
      wrapperStyle,
      name,
      register,
      registerOptions,
      errors,
      type = 'text',
      label,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      maxLength = 255,
      autoComplete,
      value,
      required,
      showPasswordToggle = false,
      multiline,
      minRows,
      maxRows,
      hideLabel = false,
      placeholder,
      InputProps: externalInputProps,
      inputProps: externalInputBaseProps,
      ...props
    },
    ref,
  ) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (errors && name) {
        const error = name.split('.').reduce((acc, key) => acc?.[key], errors)?.message;
        setErrorMessage(error || '');
      } else {
        setErrorMessage('');
      }
    }, [errors, name]);

    return (
      <Box
        className={cn(styles.inputWrapper, wrapperClassName, 'fs-unmask', disabled && styles.disabledField)}
        style={wrapperStyle}
      >
        <MuiTextField
          multiline={multiline ?? undefined}
          minRows={minRows ?? undefined}
          maxRows={maxRows ?? undefined}
          className={cn(styles.input, inputClass)}
          {...(register ? register(name, registerOptions) : {})}
          InputLabelProps={{
            className: 'fs-unmask',
            required: !hideLabel && Boolean(required),
            shrink: hideLabel ? false : Boolean(value) || undefined,
            sx: hideLabel
              ? undefined
              : {
                  fontSize: '14px',
                  backgroundColor: 'white',
                  px: 0.3,
                  color: disabled ? 'rgba(23, 36, 51, 0.38)' : 'rgba(23, 36, 51, 0.72)',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(6px, -10px) scale(0.75)',
                  },
                  '&.Mui-focused': {
                    color: 'var(--primary-color)',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#d32f2f',
                    opacity: 1,
                    fontWeight: 700,
                  },
                },
          }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline legend': {
              maxWidth: '0.01px !important',
            },
            '& .MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
              maxWidth: '0.01px !important',
            },
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s ease',
              backgroundColor: disabled ? '#f8fafc' : '#ffffff',
            },
            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: disabled ? 'rgba(23, 36, 51, 0.16)' : 'rgba(23, 36, 51, 0.24)',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: disabled ? 'rgba(23, 36, 51, 0.16)' : 'rgba(23, 36, 51, 0.72)',
            },
            '& .MuiOutlinedInput-root:hover': {
              backgroundColor: disabled ? '#f8fafc' : '#fafcff',
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              backgroundColor: '#ffffff',
              boxShadow: '0 0 0 3px rgba(46, 115, 189, 0.08)',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-color)',
              borderWidth: '2px',
            },
            '& .MuiInputBase-input': {
              color: disabled ? 'rgba(23, 36, 51, 0.45)' : 'rgba(23, 36, 51, 0.88)',
              fontWeight: disabled ? 400 : 500,
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(23, 36, 51, 0.45)',
            },
          }}
          type={showPasswordToggle && showPassword ? 'text' : type}
          ref={ref}
          inputRef={inputRef}
          fullWidth
          variant="outlined"
          label={hideLabel ? '' : label}
          disabled={disabled}
          inputProps={{
            maxLength,
            'aria-required': required || undefined,
            ...externalInputBaseProps,
          }}
          InputProps={{
            'data-testid': `input-${name}`,
            className: 'fs-exclude',
            ...(showPasswordToggle && {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }),
            ...externalInputProps,
          }}
          error={!!errorMessage}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          autoComplete={autoComplete}
          value={value}
          placeholder={placeholder}
          {...props}
        />
        {errorMessage && (
          <span
            className={styles.errorText}
            data-testid={`error-${name}`}
            style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}
          >
            {errorMessage}
          </span>
        )}
      </Box>
    );
  },
);

export default CustomTextField;
